import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Calculate as CalcIcon,
  Description as DocIcon,
  CalendarMonth as CalendarIcon,
  Checklist as CheckIcon,
  Star as StarIcon,
  People as PeopleIcon,
  TrendingUp as TrendIcon,
  Flag as FlagIcon,
  School as SchoolIcon,
  Cable as IntegrationIcon,
  Shield as ShieldIcon,
  BarChart as ChartIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { PageLayout } from '../../PageLayout';
import { getSelectedAccountsSubject, hasAdviseFull } from '../../../store/User';
import { getAdviseSupplierSubject, useAdviseSuppliers } from '../../../store/AdviseSupplier';
import { getAdviseEngagementSubject, useAdviseEngagements } from '../../../store/AdviseEngagement';
import { AdviseSupplier, AdviseEngagement } from '../../../types/advise';
import { submitAdviseDealRegistration } from '../../../Api/Advise/Engagements';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { AdviseAccountGuard } from '../../Compounds/Advise/AdviseAccountGuard';

interface AgentTool {
  id: string;
  name: string;
  description: string;
  icon: typeof BusinessIcon;
  category: string;
  status: 'available' | 'coming-soon';
  features: string[];
}

const AGENT_TOOLS: AgentTool[] = [
  {
    id: 'deal-registration',
    name: 'Deal Registration',
    description: 'Register deals and manage channel manager contacts.',
    icon: BusinessIcon,
    category: 'Sales Tools',
    status: 'available',
    features: ['Supplier Directory', 'Contact Management', 'Deal Tracking']
  },
  {
    id: 'roi-calculator',
    name: 'ROI Calculator',
    description: 'Calculate return on investment for implementations.',
    icon: CalcIcon,
    category: 'Business Analysis',
    status: 'coming-soon',
    features: ['Cost Analysis', 'Savings Projection', 'TCO Modeling']
  },
  {
    id: 'proposal-generator',
    name: 'Proposal Generator',
    description: 'Generate professional branded proposals.',
    icon: DocIcon,
    category: 'Sales Tools',
    status: 'coming-soon',
    features: ['Executive Summary', 'Pricing Tables', 'Custom Branding']
  },
  {
    id: 'demo-scheduler',
    name: 'Demo Scheduler',
    description: 'Schedule and manage supplier demonstrations.',
    icon: CalendarIcon,
    category: 'Project Management',
    status: 'coming-soon',
    features: ['Calendar Integration', 'Reminders', 'Prep Checklists']
  },
  {
    id: 'requirements-tracker',
    name: 'Requirements Tracker',
    description: 'Track and manage customer requirements.',
    icon: CheckIcon,
    category: 'Project Management',
    status: 'coming-soon',
    features: ['Requirement Mapping', 'Progress Tracking', 'Compliance']
  },
  {
    id: 'supplier-scorecard',
    name: 'Supplier Scorecard',
    description: 'Comprehensive supplier evaluation with weighted scoring.',
    icon: StarIcon,
    category: 'Evaluation',
    status: 'coming-soon',
    features: ['Weighted Scoring', 'Benchmarking', 'Export Reports']
  },
  {
    id: 'client-portal',
    name: 'Client Portal',
    description: 'Secure portal for sharing engagements and reports.',
    icon: PeopleIcon,
    category: 'Collaboration',
    status: 'coming-soon',
    features: ['Secure Access', 'Document Sharing', 'Comments']
  },
  {
    id: 'market-intelligence',
    name: 'Market Intelligence',
    description: 'Real-time market data and competitive intelligence.',
    icon: TrendIcon,
    category: 'Research',
    status: 'coming-soon',
    features: ['Market Trends', 'Pricing Intel', 'Industry Reports']
  },
  {
    id: 'implementation-planner',
    name: 'Implementation Planner',
    description: 'Plan and track implementations with milestones.',
    icon: FlagIcon,
    category: 'Implementation',
    status: 'coming-soon',
    features: ['Project Timelines', 'Resource Planning', 'Risk Mgmt']
  },
  {
    id: 'training-resources',
    name: 'Training Resources',
    description: 'Training materials and best practices.',
    icon: SchoolIcon,
    category: 'Education',
    status: 'coming-soon',
    features: ['Tutorials', 'Best Practices', 'Resource Library']
  },
  {
    id: 'integration-mapper',
    name: 'Integration Mapper',
    description: 'Map system integrations and data flows.',
    icon: IntegrationIcon,
    category: 'Technical',
    status: 'coming-soon',
    features: ['System Mapping', 'API Docs', 'Integration Testing']
  },
  {
    id: 'compliance-checker',
    name: 'Compliance Checker',
    description: 'Verify supplier compliance with standards.',
    icon: ShieldIcon,
    category: 'Compliance',
    status: 'coming-soon',
    features: ['Regulatory Compliance', 'Security Audits', 'Risk Assessment']
  },
  {
    id: 'analytics-dashboard',
    name: 'Analytics Dashboard',
    description: 'Advanced analytics with custom KPIs.',
    icon: ChartIcon,
    category: 'Analytics',
    status: 'available',
    features: ['Custom KPIs', 'Real-time Analytics', 'Data Viz']
  }
];

const CATEGORIES = [
  'All Tools',
  'Sales Tools',
  'Business Analysis',
  'Project Management',
  'Evaluation',
  'Collaboration',
  'Research',
  'Implementation',
  'Education',
  'Technical',
  'Compliance',
  'Analytics'
];

// ---- Deal Registration Sub-Component ----

interface DealRegDialogProps {
  open: boolean;
  onClose: () => void;
  suppliers: AdviseSupplier[];
  engagements: AdviseEngagement[];
}

const DealRegistrationDialog = ({ open, onClose, suppliers, engagements }: DealRegDialogProps) => {
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [engagementId, setEngagementId] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const suppliersWithContacts = useMemo(() => suppliers.filter((v) => v.channel_manager_email), [suppliers]);

  const toggleSupplier = useCallback((id: string) => {
    setSelectedSuppliers((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!companyName.trim() || selectedSuppliers.length === 0) {
      setError('Company name and at least one supplier required.');
      return;
    }
    setSending(true);
    setError('');

    const result = await submitAdviseDealRegistration(engagementId || 'none', {
      company_name: companyName,
      contact_name: contactName,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      supplier_ids: selectedSuppliers,
      engagement_id: engagementId || null
    });

    setSending(false);
    if (result.error) {
      setError(result.error.message);
    } else {
      setSuccess(true);
    }
  }, [companyName, contactName, contactEmail, contactPhone, selectedSuppliers, engagementId]);

  const handleClose = useCallback(() => {
    setCompanyName('');
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setSelectedSuppliers([]);
    setEngagementId('');
    setSuccess(false);
    setError('');
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>Register a Deal</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert
            severity="success"
            sx={{ mt: 1 }}>
            Deal registration submitted successfully.
          </Alert>
        ) : (
          <Box className="flex flex-col gap-3 mt-2">
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="subtitle2">Customer Information</Typography>
            <Box className="grid grid-cols-2 gap-3">
              <TextField
                size="small"
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                size="small"
                label="Contact Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                fullWidth
              />
            </Box>

            <Typography
              variant="subtitle2"
              sx={{ mt: 1 }}>
              Select Suppliers ({selectedSuppliers.length} selected)
            </Typography>
            <Box className="flex flex-wrap gap-1">
              {suppliersWithContacts.map((v) => (
                <Chip
                  key={v.id}
                  label={v.supplier_name}
                  variant={selectedSuppliers.includes(v.id) ? 'filled' : 'outlined'}
                  color={selectedSuppliers.includes(v.id) ? 'primary' : 'default'}
                  onClick={() => toggleSupplier(v.id)}
                  size="small"
                />
              ))}
              {suppliersWithContacts.length === 0 && (
                <Typography
                  variant="caption"
                  color="textSecondary">
                  No suppliers with channel manager contacts.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{success ? 'Close' : 'Cancel'}</Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={sending || !companyName.trim()}
            startIcon={sending ? <CircularProgress size={16} /> : null}>
            {sending ? 'Sending...' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---- Main Page ----

export const AdviseAgentToolsPage = () => {
  const [category, setCategory] = useState('All Tools');
  const [search, setSearch] = useState('');
  const [showDealReg, setShowDealReg] = useState(false);
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [engagements, setEngagements] = useState<AdviseEngagement[]>([]);

  const { fetchSuppliers } = useAdviseSuppliers();
  const { fetchEngagements } = useAdviseEngagements();
  const canEdit = hasAdviseFull();

  useEffect(() => {
    const subs = [
      getAdviseSupplierSubject().subscribe(setSuppliers),
      getAdviseEngagementSubject().subscribe(setEngagements)
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  useEffect(() => {
    fetchSuppliers();
    fetchEngagements();

    const accountSub = getSelectedAccountsSubject().subscribe(() => {
      fetchEngagements(true);
    });
    return () => accountSub.unsubscribe();
  }, []);

  const filtered = useMemo(
    () =>
      AGENT_TOOLS.filter((tool) => {
        const matchCat = category === 'All Tools' || tool.category === category;
        const q = search.toLowerCase();
        const matchSearch = !q || tool.name.toLowerCase().includes(q) || tool.description.toLowerCase().includes(q);
        return matchCat && matchSearch;
      }),
    [category, search]
  );

  const availableCount = AGENT_TOOLS.filter((t) => t.status === 'available').length;
  const comingSoonCount = AGENT_TOOLS.filter((t) => t.status === 'coming-soon').length;

  const handleToolClick = useCallback((tool: AgentTool) => {
    if (tool.status !== 'available') return;
    if (tool.id === 'deal-registration') {
      setShowDealReg(true);
    }
  }, []);

  return (
    <PageLayout
      pageTitle="Advise Tools"
      dataCy="advise-tools-page">
      <AdviseSubNav />
      <AdviseAccountGuard>
        {/* Stats */}
        <Box className="flex items-center gap-6 mb-4">
          <Box className="flex items-center gap-1">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main'
              }}
            />
            <Typography variant="caption">{availableCount} Available</Typography>
          </Box>
          <Box className="flex items-center gap-1">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'warning.main'
              }}
            />
            <Typography variant="caption">{comingSoonCount} Coming Soon</Typography>
          </Box>
        </Box>

        {/* Search + Filters */}
        <Box className="flex flex-col gap-3 mb-4">
          <TextField
            size="small"
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  fontSize="small"
                  sx={{ mr: 1, color: 'text.secondary' }}
                />
              )
            }}
            sx={{ maxWidth: 400 }}
          />
          <Box className="flex flex-wrap gap-1">
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                variant={category === cat ? 'filled' : 'outlined'}
                color={category === cat ? 'primary' : 'default'}
                onClick={() => setCategory(cat)}
              />
            ))}
          </Box>
        </Box>

        {/* Tools Grid */}
        {filtered.length === 0 ? (
          <Alert severity="info">No tools match your search.</Alert>
        ) : (
          <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((tool) => {
              const Icon = tool.icon;
              const isAvailable = tool.status === 'available';
              return (
                <Card
                  key={tool.id}
                  variant="outlined"
                  sx={{
                    opacity: isAvailable ? 1 : 0.6,
                    cursor: isAvailable ? 'pointer' : 'default',
                    '&:hover': isAvailable ? { borderColor: 'primary.main' } : {}
                  }}
                  onClick={() => handleToolClick(tool)}>
                  <CardContent>
                    <Box className="flex items-start gap-3 mb-2">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                        <Icon color={isAvailable ? 'action' : 'disabled'} />
                      </Box>
                      <Box className="flex-1 min-w-0">
                        <Typography variant="subtitle2">{tool.name}</Typography>
                        <Chip
                          label={isAvailable ? 'Available' : 'Coming Soon'}
                          size="small"
                          color={isAvailable ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom>
                      {tool.description}
                    </Typography>
                    <Box className="flex flex-wrap gap-1">
                      {tool.features.map((f) => (
                        <Chip
                          key={f}
                          label={f}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    {isAvailable && canEdit && (
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToolClick(tool);
                        }}>
                        Launch
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Deal Registration Dialog */}
        <DealRegistrationDialog
          open={showDealReg}
          onClose={() => setShowDealReg(false)}
          suppliers={suppliers}
          engagements={engagements}
        />
      </AdviseAccountGuard>
    </PageLayout>
  );
};
